import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EventPublicStats {
  event_id: string;
  occurrence_id: string | null;
  capacity_total: number;
  booked_count: number;
  seats_left: number;
  is_full: boolean;
}

/**
 * Fetch public stats for an event (capacity, booked, seats left).
 * For recurring events, pass occurrenceId to get stats for a specific occurrence.
 */
export function useEventPublicStats(
  eventId: string | undefined,
  occurrenceId: string | null = null
) {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['event-public-stats', eventId, occurrenceId],
    queryFn: async (): Promise<EventPublicStats | null> => {
      if (!eventId) return null;
      
      try {
        const { data, error } = await supabase
          .rpc('get_event_public_stats', {
            p_event_id: eventId,
            p_occurrence_id: occurrenceId,
          });
        
        if (error) {
          console.error('[useEventPublicStats] Error fetching event stats:', error);
          throw error;
        }
        
        if (import.meta.env.DEV) {
          console.log('[useEventPublicStats] RPC returned:', { data, eventId, occurrenceId });
        }
        
        // RPC returns array, we expect single row
        if (!data || data.length === 0) {
          if (import.meta.env.DEV) {
            console.warn('[useEventPublicStats] Empty response from RPC');
          }
          return null;
        }
        return data[0] as EventPublicStats;
      } catch (err) {
        console.error('[useEventPublicStats] Exception:', err);
        throw err;
      }
    },
    enabled: !!eventId,
    // Phase 1 stability: doubled from 30 s to reduce capacity-count query frequency on hot
    // public event pages. Still fresh enough for booking intent.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Helper to refetch after booking
  const refetchStats = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['event-public-stats', eventId, occurrenceId] 
    });
  };

  return {
    ...query,
    refetchStats,
  };
}
